/* ============================================================================
   WishlistSyncer — mirrors the local-first Wish List to the server so friends
   can read it on the Compare page (wish-list-screen).

   Sits inside AuthProvider so it can read sign-in state + authedFetch, and
   subscribes to the local wishlistStore via useWishlist().

   Responsibilities:
     - On sign-in: GET /api/wishlist. If the server has a list, adopt it (it is
       the cross-device source of truth). If the server is empty but this device
       has a local list (e.g. built via long-press before this sync), push it up.
     - On sign-out: clear the local list so one account's wishes never leak onto
       the next account on a shared device.
     - On local change while signed in: debounced PUT /api/wishlist.

   A missing endpoint (404/501) is a documented gap, exactly like dexStateStore's
   server sync: the local list keeps working and the Compare section degrades to
   an empty state. Nothing here ever throws into the UI.
============================================================================ */
import { useEffect, useRef } from "react";
import { useAuth } from "@/components/AuthProvider";
import {
  useWishlist,
  setWishlist,
  clearWishlist,
  getWishlist,
  type WishlistEntry,
} from "@/lib/wishlistStore";

const SAVE_DEBOUNCE_MS = 600;

interface WireWishlist {
  entries?: Array<{
    variant: string;
    dexNumber: number;
    formId?: string | null;
    addedAt?: number;
  }> | null;
}

function toWire(entries: WishlistEntry[]): WireWishlist {
  return {
    entries: entries.map((e) => ({
      variant: e.variant,
      dexNumber: e.dexNumber,
      ...(e.formId ? { formId: e.formId } : {}),
      addedAt: e.addedAt,
    })),
  };
}

function fromWire(data: WireWishlist | null | undefined): WishlistEntry[] {
  if (!data || !Array.isArray(data.entries)) return [];
  return data.entries
    .filter((e) => e && typeof e.variant === "string" && typeof e.dexNumber === "number")
    .map((e) => ({
      variant: e.variant as WishlistEntry["variant"],
      dexNumber: e.dexNumber,
      ...(e.formId ? { formId: e.formId } : {}),
      addedAt: typeof e.addedAt === "number" ? e.addedAt : 0,
    }));
}

/** Stable serialisation for echo-suppression (order matters — it's the list). */
function serialize(entries: WishlistEntry[]): string {
  return JSON.stringify(
    entries.map((e) => [e.variant, e.dexNumber, e.formId ?? ""]),
  );
}

export function WishlistSyncer() {
  const { isSignedIn, authedFetch } = useAuth();
  const wishlist = useWishlist();

  const prevSignedInRef = useRef(isSignedIn);
  // The serialised list we last pushed to (or pulled from) the server, so the
  // write effect skips redundant saves — including the pull that just happened.
  const lastSyncedRef = useRef<string | null>(null);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const pushToServer = (entries: WishlistEntry[]) => {
    if (!isSignedIn) return;
    authedFetch("/api/wishlist", {
      method: "PUT",
      body: JSON.stringify(toWire(entries)),
    }).catch((err) => {
      // Fire-and-forget: a missing endpoint or offline device is a documented
      // gap, never a UI error.
      console.warn("[wishlist] background save failed", err);
    });
  };

  // Sign-in / sign-out effect.
  useEffect(() => {
    const wasSignedIn = prevSignedInRef.current;
    prevSignedInRef.current = isSignedIn;

    if (!isSignedIn) {
      // Just signed out: clear local so the next account starts clean.
      if (wasSignedIn) {
        lastSyncedRef.current = null;
        clearWishlist();
      }
      return;
    }

    // Signed in (or initial mount while signed in): pull the saved list.
    authedFetch("/api/wishlist")
      .then(async (res) => {
        if (!res.ok) return; // 404/501 gap, or 401 — leave local as-is
        const data = (await res.json()) as WireWishlist;
        const server = fromWire(data);
        const local = getWishlist();
        if (server.length > 0) {
          // Server is the cross-device source of truth.
          lastSyncedRef.current = serialize(server);
          setWishlist(server);
        } else if (local.length > 0) {
          // Nothing on the server yet, but this device has a list — push it up.
          lastSyncedRef.current = serialize(local);
          pushToServer(local);
        } else {
          lastSyncedRef.current = serialize(local); // both empty
        }
      })
      .catch((err) => {
        console.warn("[wishlist] load from server failed", err);
      });
    // authedFetch is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn]);

  // Write effect: debounced PUT whenever the local list changes while signed in.
  useEffect(() => {
    if (!isSignedIn) return;
    const current = serialize(wishlist);
    if (lastSyncedRef.current === current) return;
    lastSyncedRef.current = current;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      saveTimerRef.current = null;
      pushToServer(getWishlist());
    }, SAVE_DEBOUNCE_MS);
    // pushToServer captures the current authedFetch/isSignedIn correctly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wishlist, isSignedIn]);

  useEffect(
    () => () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    },
    [],
  );

  return null;
}
